/**
 * CompanionRewardsScreen — free-tier Rewards hub inside the Companion App.
 *
 * Layout (mobile-first, fits inside CompanionApp's max-w-md shell):
 *   • Points hero (available / earned / spent) — from /api/stepathon/rewards/me
 *   • Active Step-a-Thon events — reuses CompanionStepathonCard's data shape
 *     to show quick-log CTA per event + tier badge share button
 *   • Rewards catalogue grid (1-col) with Redeem buttons — /api/stepathon/rewards
 *   • Redemption history (compact list)
 *   • Leaderboard preview (top 5 from first active event)
 *
 * This screen is 100% free — no subscription gate. Patients on
 * one-time-order can still earn/redeem points and log steps.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import API from "@/lib/api";
import { toast } from "sonner";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AutoStepTracker from "@/components/AutoStepTracker";
import PushOptInCard from "@/components/PushOptInCard";
import useLoyaltyRules from "@/hooks/useLoyaltyRules";
import {
  Gift, Ticket, ShoppingBag, Sparkles, Trophy, Share2, Activity,
  Plus, Crown, CheckCircle2, Copy, Medal,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const TYPE_META = {
  coupon:      { icon: Ticket,      label: "Coupon" },
  free_refill: { icon: Sparkles,    label: "Refill credit" },
  merch:       { icon: ShoppingBag, label: "Merch" },
};

function ScreenHeader({ title, subtitle }) {
  return (
    <header className="pt-1 pb-1">
      <h2 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
      {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
    </header>
  );
}

function PointsHero({ summary }) {
  return (
    <div className="rounded-3xl bg-gradient-to-br from-purple-700 via-fuchsia-700 to-pink-600 p-5 text-white shadow-lg" data-testid="companion-rewards-hero">
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Available points</div>
      <div className="mt-1 text-5xl font-black tabular-nums" data-testid="companion-points-available">
        {summary.points_available.toLocaleString()}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-xl bg-white/10 px-3 py-2">
          <div className="opacity-80 uppercase text-[9px] font-bold tracking-wider">Lifetime earned</div>
          <div className="font-bold tabular-nums text-sm" data-testid="companion-points-earned">{summary.points_earned.toLocaleString()}</div>
        </div>
        <div className="rounded-xl bg-white/10 px-3 py-2">
          <div className="opacity-80 uppercase text-[9px] font-bold tracking-wider">Spent</div>
          <div className="font-bold tabular-nums text-sm" data-testid="companion-points-spent">{summary.points_spent.toLocaleString()}</div>
        </div>
      </div>
      {/* Ambient step tracker status pill — visible only after user opts in.
          When still un-decided, the parent screen shows the full opt-in row. */}
      <AutoStepTracker compact />
      <p className="mt-3 text-[11px] opacity-90 leading-relaxed">
        1 step = 1 point · earn by logging steps on active events · spend on coupons, refill credits & merch.
      </p>
    </div>
  );
}

function QuickLogSteps({ event, onLogged }) {
  const [steps, setSteps] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const n = parseInt(steps, 10);
    if (!n || n < 1 || n > 100000) { toast.error("Enter 1–100,000 steps"); return; }
    setSubmitting(true);
    try {
      const { data } = await API.post(`/stepathon/events/${event.slug}/log`, { steps: n, source: "companion" });
      toast.success(`+${n.toLocaleString()} steps · ${data.user_total.toLocaleString()} total`);
      setSteps("");
      onLogged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't log steps");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <Input
        type="number"
        inputMode="numeric"
        value={steps}
        onChange={(e) => setSteps(e.target.value)}
        placeholder="Steps"
        className="rounded-xl bg-white/15 border-white/30 text-white placeholder-white/60 h-9 text-sm"
        data-testid={`companion-log-steps-input-${event.slug}`}
      />
      <Button
        onClick={submit}
        disabled={submitting || !steps}
        className="rounded-xl bg-white text-purple-700 hover:bg-white/90 h-9 text-xs font-bold px-3"
        data-testid={`companion-log-steps-btn-${event.slug}`}
      >
        <Plus className="w-3 h-3 mr-1" /> Log
      </Button>
    </div>
  );
}

function TierBadgeShare({ event, tierLabel }) {
  const badgeUrl = `${BACKEND}/api/stepathon/events/${event.slug}/badge.png${tierLabel ? `?tier=${encodeURIComponent(tierLabel)}` : ""}`;
  const share = async () => {
    const url = badgeUrl;
    const text = `I just hit ${tierLabel || "a new tier"} in the GLPFlo ${event.name}! 💜 Join me → https://glpflo.org/stepathon/${event.slug}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "GLPFlo Step-a-Thon", text, url });
      } else {
        await navigator.clipboard.writeText(`${text}\n${url}`);
        toast.success("Copied share text — paste anywhere!");
      }
    } catch { /* user cancelled */ }
  };
  return (
    <button
      onClick={share}
      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-white/15 hover:bg-white/25 text-white text-xs font-bold py-2"
      data-testid={`companion-share-badge-${event.slug}`}
    >
      <Share2 className="w-3.5 h-3.5" /> Share my {tierLabel || "tier"} badge
    </button>
  );
}

function EventCard({ event, onLogged }) {
  const [me, setMe] = useState(null);
  const load = useCallback(() => {
    API.get(`/stepathon/events/${event.slug}/me`)
      .then((r) => setMe(r.data))
      .catch(() => setMe(null));
  }, [event.slug]);
  useEffect(() => { load(); }, [load]);

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 p-4 text-white shadow"
      data-testid={`companion-rewards-event-${event.slug}`}
    >
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-2">
          <Activity className="w-4 h-4" />
          <span className="text-[10px] uppercase tracking-wider font-bold opacity-95">Active</span>
        </div>
        {me?.current_tier?.label && (
          <span className="text-[10px] uppercase tracking-widest font-bold bg-yellow-300/90 text-purple-900 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
            <Crown className="w-3 h-3" /> {me.current_tier.label}
          </span>
        )}
      </div>
      <div className="mt-1 font-bold text-sm">{event.name}</div>
      <div className="mt-1 text-[11px] opacity-90 tabular-nums">
        {event.total_steps.toLocaleString()} steps · {event.percent_to_goal.toFixed(1)}% of goal
      </div>
      <div className="mt-2 h-1.5 rounded-full bg-black/20 overflow-hidden">
        <div className="h-full bg-yellow-300/90" style={{ width: `${Math.min(100, event.percent_to_goal)}%` }} />
      </div>
      {me && (
        <div className="mt-2 text-[11px] opacity-95 tabular-nums">
          You: {me.my_steps.toLocaleString()} steps · {me.my_miles} mi
          {me.next_tier && (
            <span className="opacity-80"> · {me.steps_to_next.toLocaleString()} to {me.next_tier.label}</span>
          )}
        </div>
      )}
      <QuickLogSteps event={event} onLogged={() => { load(); onLogged?.(); }} />
      {me?.current_tier?.label && <TierBadgeShare event={event} tierLabel={me.current_tier.label} />}
    </div>
  );
}

function LeaderboardPreview({ event }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    if (!event) return;
    axios.get(`${BACKEND}/api/stepathon/events/${event.slug}/leaderboard?limit=5`)
      .then((r) => setRows(r.data.leaderboard || []))
      .catch(() => setRows([]));
  }, [event]);
  if (!event || rows.length === 0) return null;
  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4" data-testid="companion-leaderboard-preview">
      <div className="flex items-center justify-between mb-2">
        <div className="inline-flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Top walkers · {event.name}</h3>
        </div>
      </div>
      <ol className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.rank} className="flex items-center gap-2 text-xs" data-testid={`companion-leaderboard-row-${r.rank}`}>
            <span className={`w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${r.rank === 1 ? "bg-amber-100 text-amber-700" : r.rank === 2 ? "bg-slate-200 text-slate-700" : r.rank === 3 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"}`}>
              {r.rank}
            </span>
            <span className="flex-1 truncate text-slate-700 dark:text-slate-300 font-medium">{r.name}</span>
            <span className="tabular-nums font-bold text-slate-900 dark:text-white">{r.steps.toLocaleString()}</span>
            {r.tier?.label && <Medal className="w-3 h-3 text-purple-500" title={r.tier.label} />}
          </li>
        ))}
      </ol>
    </div>
  );
}

function RewardCard({ reward, available, onRedeem }) {
  const Meta = TYPE_META[reward.reward_type] || TYPE_META.coupon;
  const Icon = Meta.icon;
  const canAfford = available >= reward.points_cost;
  const outOfStock = reward.stock > 0 && reward.redeemed_count >= reward.stock;
  const disabled = !canAfford || outOfStock;
  const autoApplies = reward.reward_type !== "merch" && (reward.amount_dollars || 0) > 0;
  return (
    <div
      className={`rounded-2xl border p-3 flex flex-col bg-white dark:bg-slate-900 ${disabled ? "border-slate-200 opacity-70" : "border-purple-300 dark:border-purple-800"}`}
      data-testid={`companion-reward-card-${reward.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-purple-600 dark:text-purple-300 font-bold">
          <Icon className="w-3 h-3" /> {Meta.label}
        </div>
        {outOfStock && <span className="text-[9px] uppercase font-bold text-rose-600">Out</span>}
      </div>
      <h3 className="mt-1.5 text-sm font-bold text-slate-900 dark:text-white leading-tight">{reward.name}</h3>
      {reward.amount_dollars > 0 && (
        <div className="text-lg font-black text-emerald-600 dark:text-emerald-400 tabular-nums leading-none mt-1">
          ${reward.amount_dollars.toFixed(2)}
        </div>
      )}
      {autoApplies && (
        <div className="mt-1 inline-flex items-center gap-1 text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400">
          <Sparkles className="w-2.5 h-2.5" /> Auto-applies
        </div>
      )}
      <div className="mt-2 flex items-center justify-between gap-2">
        <div className="tabular-nums">
          <div className="text-base font-black text-purple-700 dark:text-pink-300">{reward.points_cost.toLocaleString()}</div>
          <div className="text-[9px] uppercase tracking-wider text-slate-500">points</div>
        </div>
        <Button
          onClick={() => onRedeem(reward)}
          disabled={disabled}
          className="rounded-full bg-purple-600 hover:bg-purple-700 text-white text-[11px] h-8 px-3"
          data-testid={`companion-reward-redeem-${reward.id}`}
        >
          {!canAfford ? `Need ${(reward.points_cost - available).toLocaleString()}` : "Redeem"}
        </Button>
      </div>
    </div>
  );
}

function CouponPill({ code }) {
  const copy = () => {
    navigator.clipboard.writeText(code).then(
      () => toast.success(`Copied ${code}`),
      () => toast.error("Could not copy")
    );
  };
  return (
    <button onClick={copy} className="inline-flex items-center gap-1 rounded-md bg-emerald-100 dark:bg-emerald-900/40 border border-emerald-300 px-1.5 py-0.5 text-[10px] font-mono text-emerald-800 dark:text-emerald-200" data-testid={`companion-copy-coupon-${code}`}>
      <Copy className="w-2.5 h-2.5" /> {code}
    </button>
  );
}

export default function CompanionRewardsScreen() {
  const [summary, setSummary] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const rules = useLoyaltyRules();
  const bonusPts = Number(rules.companion_install_bonus_points || 0);

  const load = useCallback(async () => {
    try {
      const [meR, rewardsR, eventsR] = await Promise.all([
        API.get("/stepathon/rewards/me"),
        API.get("/stepathon/rewards"),
        axios.get(`${BACKEND}/api/stepathon/events?status=active`),
      ]);
      setSummary(meR.data);
      setRewards(rewardsR.data.rewards || []);
      setEvents(eventsR.data.events || []);
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error("Couldn't load rewards");
      }
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onRedeem = async (reward) => {
    const autoApplies = reward.reward_type !== "merch" && (reward.amount_dollars || 0) > 0;
    const ok = await brandedConfirm({
      title: `Redeem "${reward.name}"?`,
      description: autoApplies
        ? `Deducts ${reward.points_cost.toLocaleString()} pts and adds a $${(reward.amount_dollars || 0).toFixed(2)} discount that auto-applies on your next refill. Redemptions cannot be undone.`
        : `Deducts ${reward.points_cost.toLocaleString()} pts. Our team will follow up on sizing/shipping. Redemptions cannot be undone.`,
      confirmText: "Redeem",
      cancelText: "Not yet",
      tone: "info",
    });
    if (!ok) return;
    try {
      const r = await API.post(`/stepathon/rewards/${reward.id}/redeem`);
      if (r.data.auto_apply) {
        toast.success(`Redeemed! $${(r.data.amount_dollars || 0).toFixed(2)} auto-applies at checkout.`, { duration: 6000 });
      } else if (r.data.coupon_code) {
        toast.success(`Redeemed! Code: ${r.data.coupon_code}`);
      } else {
        toast.success("Redeemed! We'll follow up by email.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Redeem failed");
    }
  };

  if (loading || !summary) {
    return <div className="p-4 text-sm text-slate-500" data-testid="companion-rewards-loading">Loading your rewards…</div>;
  }

  return (
    <div className="p-4 space-y-4" data-testid="companion-rewards">
      <ScreenHeader title="Rewards" subtitle="Free · walk to earn points, spend on coupons + merch" />

      {/* One-time auto-tracker opt-in (hides itself after decision). */}
      <AutoStepTracker />

      {/* Daily rewards push nudge opt-in (hides itself after decision). */}
      <PushOptInCard />

      <PointsHero summary={summary} />

      {events.length > 0 && (
        <div className="space-y-3">
          {events.slice(0, 3).map((ev) => (
            <EventCard key={ev.slug} event={ev} onLogged={load} />
          ))}
        </div>
      )}

      {bonusPts > 0 && (
        <div
          className="rounded-2xl bg-gradient-to-br from-amber-300 to-orange-400 p-4 text-amber-950 shadow"
          data-testid="companion-refer-earn-callout"
        >
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
            <Gift className="w-3.5 h-3.5" /> Refer + earn
          </div>
          <h3 className="mt-1 text-base font-black leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            +{bonusPts.toLocaleString()} bonus points for every friend who signs up
          </h3>
          <p className="mt-1 text-[11px] leading-relaxed">
            Credited the moment they register — no purchase required. Both of you still get a ${Number(rules.referral_amount || 20)} off coupon.
          </p>
        </div>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 inline-flex items-center gap-1.5">
          <Gift className="w-3.5 h-3.5 text-purple-500" /> Redeem your points
        </h3>
        {rewards.length === 0 ? (
          <div className="rounded-2xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4 text-xs text-slate-500 text-center" data-testid="companion-rewards-empty">
            No rewards available yet — check back after our next drop.
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="companion-rewards-grid">
            {rewards.map((r) => (
              <RewardCard key={r.id} reward={r} available={summary.points_available} onRedeem={onRedeem} />
            ))}
          </div>
        )}
      </section>

      {events.length > 0 && <LeaderboardPreview event={events[0]} />}

      {summary.history && summary.history.length > 0 && (
        <section data-testid="companion-rewards-history">
          <h3 className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Recent redemptions</h3>
          <ul className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
            {summary.history.slice(0, 5).map((h) => (
              <li key={h.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold truncate text-slate-900 dark:text-white">{h.reward_name}</div>
                  {h.coupon_code && <div className="mt-0.5"><CouponPill code={h.coupon_code} /></div>}
                </div>
                <div className="text-right">
                  <div className="text-[11px] font-bold tabular-nums text-slate-900 dark:text-white">−{h.points_spent.toLocaleString()}</div>
                  {h.status === "fulfilled" ? (
                    <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-600 uppercase tracking-wider"><CheckCircle2 className="w-2.5 h-2.5" /> Done</span>
                  ) : (
                    <span className="text-[9px] font-bold text-amber-600 uppercase tracking-wider">{h.status}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
